// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = (obj) => Object.keys(obj).length === 0;

// ** International org check. country is an ISO 3166-1 alpha-2 code; a NULL/empty
// ** country is treated as IN (legacy default), so only an explicit non-IN code
// ** counts as an international org.
export const isIntlOrg = (country) =>
  !!country && country.toUpperCase() !== "IN";

// ** Maps an ISO 3166-1 alpha-2 country code to its ISO 4217 currency code.
// ** A NULL/empty country is treated as IN (legacy default) → INR.
const COUNTRY_CURRENCY = {
  IN: "INR",
  US: "USD",
  CA: "CAD",
  GB: "GBP",
  AU: "AUD",
  NZ: "NZD",
  SG: "SGD",
  AE: "AED",
  SA: "SAR",
  ZA: "ZAR",
};

export const getCurrencyForCountry = (country) => {
  if (!country) return "INR";
  return COUNTRY_CURRENCY[country.toUpperCase()] || "INR";
};

// ** Returns just the currency symbol (e.g. "₹", "$") for the given currency code.
export const getCurrencySymbol = (currency = "INR") => {
  try {
    const parts = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0);
    return parts.find((p) => p.type === "currency")?.value ?? currency;
  } catch {
    return currency;
  }
};

// ** Formats an amount with its currency symbol (e.g. "₹1,200", "$1,200").
export const formatCurrencyAmount = (amount, currency) => {
  const code = currency || "INR";
  // For USD, use the narrow symbol so it renders "$" instead of "US$".
  // Other currencies keep the disambiguating prefix (e.g. CAD → "CA$").
  const currencyDisplay =
    code.toUpperCase() === "USD" ? "narrowSymbol" : "symbol";
  const value = typeof amount === "string" ? Number(amount) : amount;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      currencyDisplay,
    }).format(value);
  } catch {
    return `${code} ${value}`;
  }
};

// ** Convenience: currency symbol for an org's country (e.g. "IN" → "₹").
export const getCountryCurrencySymbol = (country) =>
  getCurrencySymbol(getCurrencyForCountry(country));

// ** Convenience: formats an amount in the org's country currency.
export const formatCountryAmount = (amount, country) =>
  formatCurrencyAmount(amount, getCurrencyForCountry(country));

// ** Returns K format from a number
export const kFormatter = (num) =>
  num > 999 ? `${(num / 1000).toFixed(1)}k` : num;

// ** Converts HTML to string
export const htmlToString = (html) => html.replace(/<\/?[^>]+(>|$)/g, "");

// ** Checks if the passed date is today
const isToday = (date) => {
  const today = new Date();
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  );
};

/**
 ** Format and return date in Humanize format
 ** Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 ** Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String} value date to format
 * @param {Object} formatting Intl object to format with
 */
export const formatDate = (
  value,
  formatting = { month: "short", day: "numeric", year: "numeric" },
) => {
  if (!value) return value;
  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value));
};

// ** Returns short month of passed date
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
  const date = new Date(value);
  let formatting = { month: "short", day: "numeric" };

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: "numeric", minute: "numeric" };
  }

  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value));
};

/**
 ** Return if user is logged in
 ** This is completely up to you and how you want to store the token in your frontend application
 *  ? e.g. If you are using cookies to store the application please update this function
 */
export const isUserLoggedIn = () => localStorage.getItem("userData");
export const getUserData = () => JSON.parse(localStorage.getItem("userData"));

/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note role field is just for showing purpose it's not used by anything in frontend
 ** We are checking role just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
export const getHomeRouteForLoggedInUser = (userRole) => {
  return userRole === "ADMIN" || userRole === "SU" ? "/" : "/login";
};

/**
 ** Access level of the logged-in admin user ("SU" | "ADMIN" | "AUDITOR").
 *  Login stores the API response as-is, so the profile sits under `.userData`.
 *  The top-level fallback keeps this working for older stored payloads.
 */
export const getUserAccessLevel = () => {
  try {
    const data = getUserData();
    return data?.userData?.access_level || data?.access_level || null;
  } catch {
    return null;
  }
};

export const isSuperUser = () => getUserAccessLevel() === "SU";

/** Shown wherever a non-SU user tries to edit an SU-only setting. */
export const SU_ONLY_MESSAGE = "You are not a SU user, you can't edit!";

export const colors = {
  primary25: "#1E48F71a", // for option hover bg-color
  primary: "#1E48F7", // for selected option bg-color
  neutral10: "#1E48F7", // for tags bg-color
  neutral20: "#ededed", // for input border-color
  neutral30: "#ededed", // for input hover border-color
};

// ** React Select Theme Colors
export const selectThemeColors = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    ...colors,
  },
});

export const removeDuplicates = (array, id) => {
  let newArray = [];
  let uniqueObject = {};

  for (let i in array) {
    const objTitle = array[i][id];
    uniqueObject[objTitle] = array[i];
  }

  for (let i in uniqueObject) {
    newArray.push(uniqueObject[i]);
  }

  return newArray;
};

export const countWords = (s) => {
  if (s && typeof s === "string") {
    s = s.replace(/(^\s*)|(\s*$)/gi, "");
    s = s.replace(/[ ]{2,}/gi, " ");
    s = s.replace(/\n /, "\n");
    return s.split(" ").filter(function (str) {
      return str !== "";
    }).length;
  }
  return 0;
};

export const comparePhoneNumbers = (phone1, phone2) => {
  if (typeof phone1 === "string" && typeof phone2 === "string") {
    return phone1.replace("+91", "") === phone2.replace("+91", "");
  }
  return false;
};
